import React, { useState } from 'react';
import { DETAIL_STATUS_FIELDS, DetailStatusRow, ThemeColors } from './types';

type DocumentDetailBarplotProps = {
    title: string;
    data: DetailStatusRow[];
    height?: number;
    themeColors: ThemeColors;
};

export const DocumentDetailBarplot: React.FC<DocumentDetailBarplotProps> = ({
    title,
    data,
    height = 360,
    themeColors
}) => {
    const [hoveredBar, setHoveredBar] = useState<{ index: number; field: string } | null>(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

    if (!data || data.length === 0) {
        return (
            <div style={{
                backgroundColor: themeColors.cardBackground,
                borderRadius: 12,
                padding: 24,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${themeColors.cardBorder}`
            }}>
                <div style={{ textAlign: 'center', color: themeColors.textSecondary, padding: 40 }}>
                    No data available
                </div>
            </div>
        );
    }

    const barColors = ['#3b82f6', '#4f46e5', '#7c3aed', '#a855f7'];
    const getBarColor = (index: number) => barColors[index] || '#8b5cf6';

    const maxValue = Math.max(...data.flatMap(item =>
        DETAIL_STATUS_FIELDS.map(field => (item[field.key] as number) || 0)
    ));

    const formatNumber = (num: number) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return num.toLocaleString();
    };

    const paddingTop = 40;
    const paddingBottom = 60;
    const paddingLeft = 260;
    const paddingRight = 40;
    const barGap = 0;
    const groupGap = 18;
    const barsPerGroup = DETAIL_STATUS_FIELDS.length;
    const barHeight = 12;
    const groupHeight = barHeight * barsPerGroup + barGap * (barsPerGroup - 1);
    const totalRowsHeight = groupHeight * data.length + groupGap * Math.max(0, data.length - 1);
    const effectiveHeight = Math.max(height, paddingTop + paddingBottom + totalRowsHeight);
    const availableHeight = effectiveHeight - paddingTop - paddingBottom;

    const handleBarHover = (index: number, field: string, event: React.MouseEvent) => {
        setHoveredBar({ index, field });
        const rect = event.currentTarget.getBoundingClientRect();
        setTooltipPos({
            x: rect.left + rect.width / 2,
            y: rect.top - 10
        });
    };

    return (
        <div style={{
            backgroundColor: themeColors.cardBackground,
            borderRadius: 12,
            padding: 24,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: `1px solid ${themeColors.cardBorder}`,
            position: 'relative'
        }}>
            <div style={{
                fontSize: 15,
                fontWeight: 600,
                color: themeColors.text,
                marginBottom: 20
            }}>
                {title}
            </div>

            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 16,
                marginBottom: 20,
                fontSize: 12
            }}>
                {DETAIL_STATUS_FIELDS.map((field, idx) => (
                    <div key={field.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                            width: 12,
                            height: 12,
                            borderRadius: 2,
                            background: barColors[idx] || '#8b5cf6'
                        }} />
                        <span style={{ color: themeColors.textSecondary }}>{field.label}</span>
                    </div>
                ))}
            </div>

            <div style={{
                position: 'relative',
                height: effectiveHeight,
                width: '100%'
            }}>
                <div style={{
                    position: 'absolute',
                    left: 0,
                    top: paddingTop,
                    width: paddingLeft - 10,
                    height: availableHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    gap: groupGap
                }}>
                    {data.map((item, index) => (
                        <div key={index} style={{
                            fontSize: 11,
                            color: themeColors.text,
                            textAlign: 'right',
                            paddingRight: 10,
                            height: groupHeight,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            fontWeight: 500,
                            wordBreak: 'break-word'
                        }}>
                            {item.tipo_documento}
                        </div>
                    ))}
                </div>

                <svg
                    style={{
                        position: 'absolute',
                        left: paddingLeft,
                        top: paddingTop,
                        width: `calc(100% - ${paddingLeft + paddingRight}px)`,
                        height: availableHeight,
                        pointerEvents: 'none'
                    }}
                >
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                        <line
                            key={i}
                            x1={`${ratio * 100}%`}
                            y1="0"
                            x2={`${ratio * 100}%`}
                            y2="100%"
                            stroke={themeColors.gridLine}
                            strokeWidth="1"
                            strokeDasharray="4,4"
                        />
                    ))}
                </svg>

                <div style={{
                    position: 'absolute',
                    left: paddingLeft,
                    right: paddingRight,
                    bottom: paddingBottom - 30,
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    color: themeColors.textSecondary
                }}>
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
                        <span key={ratio}>
                            {formatNumber(Math.round(maxValue * ratio))}
                        </span>
                    ))}
                </div>

                <div style={{
                    position: 'absolute',
                    left: paddingLeft,
                    top: paddingTop,
                    right: paddingRight,
                    height: availableHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    gap: groupGap
                }}>
                    {data.map((item, index) => (
                        <div key={index} style={{
                            height: groupHeight,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: barGap
                        }}>
                            {DETAIL_STATUS_FIELDS.map((field, fieldIndex) => {
                                const value = (item[field.key] as number) || 0;
                                const barWidth = maxValue > 0 ? (value / maxValue) * 100 : 0;
                                const barColor = getBarColor(fieldIndex);

                                return (
                                    <div
                                        key={field.key}
                                        onMouseEnter={(e) => handleBarHover(index, field.key, e)}
                                        onMouseLeave={() => setHoveredBar(null)}
                                        style={{
                                            height: barHeight,
                                            width: `${barWidth}%`,
                                            backgroundColor: barColor,
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            opacity: hoveredBar?.index === index && hoveredBar?.field === field.key ? 0.85 : 1,
                                            transform: hoveredBar?.index === index && hoveredBar?.field === field.key
                                                ? 'scaleY(1.1)'
                                                : 'scaleY(1)',
                                            boxShadow: hoveredBar?.index === index && hoveredBar?.field === field.key
                                                ? `0 2px 8px ${barColor}80`
                                                : 'none'
                                        }}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {hoveredBar !== null && (
                <div style={{
                    position: 'fixed',
                    left: tooltipPos.x,
                    top: tooltipPos.y,
                    transform: 'translate(-50%, -100%)',
                    backgroundColor: 'rgba(17, 24, 39, 0.95)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)'
                }}>
                    <div style={{ marginBottom: 4, fontWeight: 600 }}>
                        {data[hoveredBar.index].tipo_documento}
                    </div>
                    <div style={{ fontSize: 12 }}>
                        {DETAIL_STATUS_FIELDS.find(f => f.key === hoveredBar.field)?.label}: {' '}
                        {formatNumber((data[hoveredBar.index][hoveredBar.field as keyof DetailStatusRow] as number) || 0)}
                    </div>
                </div>
            )}
        </div>
    );
};
